import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, Utensils, LogOut, Search, Filter, ChevronDown, 
  Mail, Phone, MapPin, Award, ChefHat, Clock,
  Check, X, Eye, FileText, Trash2, Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboardPage() {
  const { token, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('chefs');
  const [stats, setStats] = useState(null);
  const [chefs, setChefs] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChef, setSelectedChef] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showMatchModal, setShowMatchModal] = useState(null);
  const [filters, setFilters] = useState({
    experience_level: '',
    pizza_types: [],
    oven_types: [],
    languages: [],
    available_countries: []
  });

  const fetchData = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [statsRes, chefsRes, restaurantsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/chefs`, { headers }),
        axios.get(`${API}/admin/restaurants`, { headers })
      ]);
      setStats(statsRes.data);
      setChefs(chefsRes.data.chefs);
      setRestaurants(restaurantsRes.data.requests);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/admin');
      }
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate, fetchData]);

  const applyFilters = async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const filterPayload = {};
      if (filters.experience_level) filterPayload.experience_level = filters.experience_level;
      if (filters.pizza_types.length) filterPayload.pizza_types = filters.pizza_types;
      if (filters.oven_types.length) filterPayload.oven_types = filters.oven_types;
      if (filters.languages.length) filterPayload.languages = filters.languages;
      if (filters.available_countries.length) filterPayload.available_countries = filters.available_countries;

      const response = await axios.post(`${API}/admin/chefs/filter`, filterPayload, { headers });
      setChefs(response.data.chefs);
      toast.success(`${response.data.total} candidati trovati`);
    } catch (error) {
      toast.error('Errore nella ricerca');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = async () => {
    setFilters({
      experience_level: '',
      pizza_types: [],
      oven_types: [],
      languages: [],
      available_countries: []
    });
    await fetchData();
  };

  const updateChefStatus = async (chefId, status) => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.patch(`${API}/admin/chefs/${chefId}/status?status=${status}`, {}, { headers });
      toast.success('Stato aggiornato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const deleteChef = async (chefId) => {
    if (!token) return;
    if (!window.confirm('Sei sicuro di voler eliminare questa candidatura?')) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(`${API}/admin/chefs/${chefId}`, { headers });
      toast.success('Candidatura eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const updateRestaurantStatus = async (requestId, status) => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.patch(`${API}/admin/restaurants/${requestId}/status?status=${status}`, {}, { headers });
      toast.success('Stato aggiornato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const deleteRestaurant = async (requestId) => {
    if (!token) return;
    if (!window.confirm('Sei sicuro di voler eliminare questa richiesta?')) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(`${API}/admin/restaurants/${requestId}`, { headers });
      toast.success('Richiesta eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const sendAnonymousMatch = (restaurant, chef) => {
    const subject = encodeURIComponent('Proposta Candidato Pizzaiolo - Artisan Pizza Talent');
    const body = encodeURIComponent(`Nome candidato: ${chef.name}
Esperienza: ${chef.experience_level} anni`);
    
    // Create the mailto link
    const mailtoLink = `mailto:${restaurant.email}?cc=giacomo@artisanpizzatalent.com&subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
    setShowMatchModal(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const experienceLevels = [
    { value: '0-1', label: '0-1 anni' },
    { value: '1-2', label: '1-2 anni' },
    { value: '2-5', label: '2-5 anni' },
    { value: '5-10', label: '5-10 anni' },
    { value: '10+', label: '10+ anni' }
  ];

  const pizzaTypes = [
    { value: 'napoletana', label: 'Napoletana' },
    { value: 'romana', label: 'Romana' },
    { value: 'contemporanea', label: 'Contemporanea' },
    { value: 'teglia', label: 'Teglia' },
    { value: 'pinsa', label: 'Pinsa' }
  ];

  const ovenTypes = [
    { value: 'legna', label: 'Legna' },
    { value: 'gas', label: 'Gas' },
    { value: 'elettrico', label: 'Elettrico' },
    { value: 'misto', label: 'Misto' }
  ];

  const countries = [
    { value: 'all_europe', label: 'Tutta Europa' },
    { value: 'italy', label: 'Italia' },
    { value: 'france', label: 'Francia' },
    { value: 'germany', label: 'Germania' },
    { value: 'switzerland', label: 'Svizzera' },
    { value: 'spain', label: 'Spagna' },
    { value: 'uk', label: 'UK' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'In Attesa', color: 'bg-yellow-500/20 text-yellow-500' },
    { value: 'selected', label: 'Selezionato', color: 'bg-green-500/20 text-green-500' },
    { value: 'sent', label: 'Inviato', color: 'bg-blue-500/20 text-blue-500' },
    { value: 'hired', label: 'Assunto', color: 'bg-primary/20 text-primary' },
    { value: 'rejected', label: 'Non Desiderato', color: 'bg-red-500/20 text-red-500' }
  ];

  const getStatusStyle = (status) => {
    const option = statusOptions.find(s => s.value === status);
    return option ? option.color : 'bg-muted text-muted-foreground';
  };

  const getStatusLabel = (status) => {
    const option = statusOptions.find(s => s.value === status);
    return option ? option.label : status;
  };

  const handleMultiFilter = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-card border-b border-primary/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xl font-serif">A</span>
            </div>
            <span className="text-foreground font-serif text-xl">Admin Dashboard</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="btn-logout"
          >
            <LogOut size={18} />
            Esci
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
            <div className="bg-card border border-primary/10 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="text-primary" size={24} />
                <span className="text-3xl font-serif text-foreground">{stats.total_chefs}</span>
              </div>
              <p className="text-sm text-muted-foreground">Pizzaioli Totali</p>
            </div>
            <div className="bg-card border border-primary/10 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="text-yellow-500" size={24} />
                <span className="text-3xl font-serif text-foreground">{stats.pending_chefs}</span>
              </div>
              <p className="text-sm text-muted-foreground">In Attesa</p>
            </div>
            <div className="bg-card border border-primary/10 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-2">
                <Utensils className="text-primary" size={24} />
                <span className="text-3xl font-serif text-foreground">{stats.total_restaurants}</span>
              </div>
              <p className="text-sm text-muted-foreground">Richieste Ristoranti</p>
            </div>
            <div className="bg-card border border-primary/10 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="text-yellow-500" size={24} />
                <span className="text-3xl font-serif text-foreground">{stats.pending_restaurants}</span>
              </div>
              <p className="text-sm text-muted-foreground">Richieste in Attesa</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('chefs')}
            className={`flex items-center gap-2 px-6 py-3 rounded-sm font-medium transition-all ${
              activeTab === 'chefs'
                ? 'bg-primary text-primary-foreground'
                : 'bg-card border border-primary/10 text-foreground hover:border-primary/30'
            }`}
            data-testid="tab-chefs"
          >
            <ChefHat size={18} />
            Pizzaioli
          </button>
          <button
            onClick={() => setActiveTab('restaurants')}
            className={`flex items-center gap-2 px-6 py-3 rounded-sm font-medium transition-all ${
              activeTab === 'restaurants'
                ? 'bg-primary text-primary-foreground'
                : 'bg-card border border-primary/10 text-foreground hover:border-primary/30'
            }`}
            data-testid="tab-restaurants"
          >
            <Utensils size={18} />
            Ristoranti
          </button>
        </div>

        {/* Chefs Tab */}
        {activeTab === 'chefs' && (
          <div>
            {/* Filters */}
            <div className="bg-card border border-primary/10 rounded-sm p-6 mb-6" data-testid="filters-panel">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-foreground font-medium mb-4"
              >
                <Filter size={18} />
                Filtri di Ricerca
                <ChevronDown size={18} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>

              {showFilters && (
                <div className="space-y-6">
                  {/* Experience Filter */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">Esperienza</label>
                    <div className="flex flex-wrap gap-2">
                      {experienceLevels.map(level => (
                        <button
                          key={level.value}
                          onClick={() => setFilters({ ...filters, experience_level: filters.experience_level === level.value ? '' : level.value })}
                          className={`px-4 py-2 text-sm rounded-sm border transition-all ${
                            filters.experience_level === level.value
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'border-primary/20 text-foreground hover:border-primary/40'
                          }`}
                          data-testid={`filter-exp-${level.value}`}
                        >
                          {level.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Pizza Types Filter */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">Tipo Pizza</label>
                    <div className="flex flex-wrap gap-2">
                      {pizzaTypes.map(type => (
                        <button
                          key={type.value}
                          onClick={() => handleMultiFilter('pizza_types', type.value)}
                          className={`px-4 py-2 text-sm rounded-sm border transition-all ${
                            filters.pizza_types.includes(type.value)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'border-primary/20 text-foreground hover:border-primary/40'
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Oven Types Filter */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">Tipo Forno</label>
                    <div className="flex flex-wrap gap-2">
                      {ovenTypes.map(type => (
                        <button
                          key={type.value}
                          onClick={() => handleMultiFilter('oven_types', type.value)}
                          className={`px-4 py-2 text-sm rounded-sm border transition-all ${
                            filters.oven_types.includes(type.value)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'border-primary/20 text-foreground hover:border-primary/40'
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Countries Filter */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">Disponibilità Paesi</label>
                    <div className="flex flex-wrap gap-2">
                      {countries.map(country => (
                        <button
                          key={country.value}
                          onClick={() => handleMultiFilter('available_countries', country.value)}
                          className={`px-4 py-2 text-sm rounded-sm border transition-all ${
                            filters.available_countries.includes(country.value)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'border-primary/20 text-foreground hover:border-primary/40'
                          }`}
                        >
                          {country.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Filter Actions */}
                  <div className="flex gap-4 pt-4 border-t border-primary/10">
                    <button
                      onClick={applyFilters}
                      className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-sm font-medium hover:brightness-110 transition-all"
                      data-testid="btn-apply-filters"
                    >
                      <Search size={18} />
                      Cerca
                    </button>
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-2 border border-primary/20 text-foreground px-6 py-3 rounded-sm font-medium hover:border-primary/40 transition-all"
                      data-testid="btn-clear-filters"
                    >
                      <X size={18} />
                      Resetta
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Chefs List */}
            <div className="grid gap-4" data-testid="chefs-list">
              {chefs.length === 0 ? (
                <div className="bg-card border border-primary/10 rounded-sm p-12 text-center">
                  <Users className="text-muted-foreground mx-auto mb-4" size={48} />
                  <p className="text-muted-foreground">Nessun pizzaiolo trovato</p>
                </div>
              ) : (
                chefs.map(chef => (
                  <div
                    key={chef.id}
                    className="bg-card border border-primary/10 rounded-sm p-6 hover:border-primary/30 transition-all"
                    data-testid={`chef-card-${chef.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-serif text-foreground">{chef.name}</h3>
                          <select
                            value={chef.status}
                            onChange={(e) => updateChefStatus(chef.id, e.target.value)}
                            className={`px-2 py-1 text-xs rounded-sm border-0 cursor-pointer ${getStatusStyle(chef.status)}`}
                          >
                            {statusOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                          <span className="flex items-center gap-1">
                            <Award size={14} className="text-primary" />
                            {chef.experience_level} anni
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail size={14} />
                            {chef.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone size={14} />
                            {chef.phone}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {chef.current_country}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {chef.pizza_types?.map(type => (
                            <span key={type} className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-sm">
                              {type}
                            </span>
                          ))}
                          {chef.oven_types?.map(type => (
                            <span key={type} className="px-2 py-1 text-xs bg-secondary text-secondary-foreground rounded-sm">
                              {type}
                            </span>
                          ))}
                          {chef.cv_url && (
                            <span className="px-2 py-1 text-xs bg-green-500/20 text-green-500 rounded-sm flex items-center gap-1">
                              <FileText size={12} />
                              CV
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedChef(chef)}
                          className="p-2 border border-primary/20 rounded-sm text-foreground hover:border-primary/40 transition-all"
                          title="Visualizza"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => deleteChef(chef.id)}
                          className="p-2 bg-red-500/20 text-red-500 rounded-sm hover:bg-red-500/30 transition-all"
                          title="Elimina"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Restaurants Tab */}
        {activeTab === 'restaurants' && (
          <div className="grid gap-4" data-testid="restaurants-list">
            {restaurants.length === 0 ? (
              <div className="bg-card border border-primary/10 rounded-sm p-12 text-center">
                <Utensils className="text-muted-foreground mx-auto mb-4" size={48} />
                <p className="text-muted-foreground">Nessuna richiesta trovata</p>
              </div>
            ) : (
              restaurants.map(request => (
                <div
                  key={request.id}
                  className="bg-card border border-primary/10 rounded-sm p-6 hover:border-primary/30 transition-all"
                  data-testid={`restaurant-card-${request.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-serif text-foreground">{request.restaurant_name}</h3>
                        <select
                          value={request.status}
                          onChange={(e) => updateRestaurantStatus(request.id, e.target.value)}
                          className={`px-2 py-1 text-xs rounded-sm border-0 cursor-pointer ${getStatusStyle(request.status)}`}
                        >
                          {statusOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {request.city}, {request.country}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail size={14} />
                          {request.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone size={14} />
                          {request.phone}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-sm">
                          {request.pizza_type_needed}
                        </span>
                        <span className="px-2 py-1 text-xs bg-secondary text-secondary-foreground rounded-sm">
                          {request.oven_type}
                        </span>
                        <span className="px-2 py-1 text-xs bg-secondary text-secondary-foreground rounded-sm">
                          {request.experience_required} anni richiesti
                        </span>
                        <span className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-sm">
                          {request.salary_range}
                        </span>
                        {request.accommodation_provided && (
                          <span className="px-2 py-1 text-xs bg-green-500/20 text-green-500 rounded-sm">
                            Alloggio incluso
                          </span>
                        )}
                      </div>
                      {request.additional_notes && (
                        <p className="text-sm text-muted-foreground italic">"{request.additional_notes}"</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const subject = encodeURIComponent('Comunicazione da Artisan Pizza Talent');
                          window.location.href = `mailto:${request.email}?cc=giacomo@artisanpizzatalent.com&subject=${subject}`;
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-sm hover:brightness-110 transition-all"
                        title="Contatta Ristoratore"
                      >
                        <Mail size={16} />
                        Contatta
                      </button>
                      <button
                        onClick={() => setShowMatchModal(request)}
                        className="flex items-center gap-2 px-4 py-2 border border-primary/30 text-primary rounded-sm hover:bg-primary/10 transition-all"
                        title="Invia Match Anonimo"
                      >
                        <Send size={16} />
                        Match
                      </button>
                      <button
                        onClick={() => deleteRestaurant(request.id)}
                        className="p-2 bg-red-500/20 text-red-500 rounded-sm hover:bg-red-500/30 transition-all"
                        title="Elimina"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Chef Detail Modal */}
      {selectedChef && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" onClick={() => setSelectedChef(null)}>
          <div className="bg-card border border-primary/10 rounded-sm max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif text-foreground">{selectedChef.name}</h2>
              <button onClick={() => setSelectedChef(null)} className="text-muted-foreground hover:text-foreground">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Email</label>
                  <p className="text-foreground">{selectedChef.email}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Telefono</label>
                  <p className="text-foreground">{selectedChef.phone}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Esperienza</label>
                  <p className="text-foreground">{selectedChef.experience_level} anni</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Paese Attuale</label>
                  <p className="text-foreground">{selectedChef.current_country}</p>
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground">Tipi di Pizza</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedChef.pizza_types?.map(type => (
                    <span key={type} className="px-3 py-1 bg-primary/10 text-primary rounded-sm">
                      {type}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground">Tipi di Forno</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedChef.oven_types?.map(type => (
                    <span key={type} className="px-3 py-1 bg-secondary text-secondary-foreground rounded-sm">
                      {type}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground">Lingue</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedChef.languages?.map(lang => (
                    <span key={lang} className="px-3 py-1 bg-secondary text-secondary-foreground rounded-sm">
                      {lang}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground">Paesi Disponibili</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedChef.available_countries?.map(country => (
                    <span key={country} className="px-3 py-1 bg-primary/10 text-primary rounded-sm">
                      {country}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground">Bio</label>
                <p className="text-foreground mt-2 leading-relaxed">{selectedChef.bio}</p>
              </div>

              {/* CV Download */}
              {selectedChef.cv_url && (
                <div>
                  <label className="text-sm text-muted-foreground">Curriculum Vitae</label>
                  <div className="mt-2">
                    <a
                      href={selectedChef.cv_url}
                      download={`CV_${selectedChef.name.replace(/\s+/g, '_')}.pdf`}
                      className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-sm hover:brightness-110 transition-all"
                    >
                      <FileText size={18} />
                      Scarica CV
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Match Modal */}
      {showMatchModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" onClick={() => setShowMatchModal(null)}>
          <div className="bg-card border border-primary/10 rounded-sm max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif text-foreground">Invia Match Anonimo</h2>
              <button onClick={() => setShowMatchModal(null)} className="text-muted-foreground hover:text-foreground">
                <X size={24} />
              </button>
            </div>
            
            <p className="text-muted-foreground mb-6">
              Seleziona un pizzaiolo da proporre a <strong>{showMatchModal.restaurant_name}</strong>. 
              Verranno inviati solo i dati anonimi (esperienza, competenze) senza contatti diretti.
            </p>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {chefs.filter(c => c.status !== 'rejected').map(chef => (
                <div
                  key={chef.id}
                  className="flex items-center justify-between p-4 bg-background border border-primary/10 rounded-sm hover:border-primary/30 transition-all"
                >
                  <div>
                    <h4 className="font-medium text-foreground">{chef.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {chef.experience_level} anni • {chef.pizza_types?.join(', ')}
                    </p>
                  </div>
                  <button
                    onClick={() => sendAnonymousMatch(showMatchModal, chef)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-sm hover:brightness-110 transition-all"
                  >
                    <Send size={16} />
                    Invia
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}